import React, { useEffect, useState } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import ClimateReport from '../../ClimateReport';
import assignmentPlanQuery from './assignmentPlan.graphql';

function ClimateTab({ currTimepoint, renderTimepointPills }) {
    const [climatePlanData, setClimatePlanData] = useState(null);

    useEffect(() => {
        // Do we even have an Assignment Plan that is:
        // - has Climate surveys
        // - is open already
        // - selected by some Class
        async function checkIsTpOpen() {
            const resp = await client.query({
                query: assignmentPlanQuery,
                fetchPolicy: 'network-only',
                variables: { timepoint: currTimepoint },
            });

            const availableAssignmentPlanList = _get(resp, 'data.one.class.availableAssignmentPlanList', []) || [];

            const climatePlanMap = availableAssignmentPlanList.reduce((all, curr) => {
                const surveyHandles = _get(curr, 'session.settings.options', []) || [];
                if (!surveyHandles.includes('climate')) {
                    return all;
                }

                let opensDaysAgo = _get(curr, 'session.opensDaysAgo');
                const opensDate = _get(curr, 'session.opensDate');
                if (!opensDaysAgo && opensDaysAgo !== 0) {
                    // if opensDate is not defined, opensDaysAgo will also not be defined, so handle this as "always open"
                    opensDaysAgo = opensDate ? -1 : 1;
                }
                const hasOpened = opensDaysAgo >= 0;
                const id = _get(curr, 'assignmentPlanId');
                all[id] = {
                    hasOpened,
                    opensDate,
                    opensOn: '',
                    isSelected: false,
                };

                return all;
            }, {});

            const allClasses = _get(resp, 'data.all.classList', []) || [];
            allClasses.forEach(c => {
                const selectedId = _get(c, 'selectedAssignmentPlan.assignmentPlanId');
                if (!selectedId || Number.isNaN(parseInt(selectedId, 10)) || !(selectedId in climatePlanMap)) {
                    return;
                }

                climatePlanMap[selectedId].isSelected = true;
            });

            setClimatePlanData(climatePlanMap);
        }

        checkIsTpOpen();
    }, [currTimepoint]);

    function renderAlert(msg) {
        return (
            <>
                {renderTimepointPills()}
                <div className="alert alert-info">
                    <p className="mb-0 text-info-emphasis">{msg}</p>
                </div>
            </>
        );
    }

    if (climatePlanData === null) {
        return <Loader loading />;
    }

    if (Object.keys(climatePlanData).length <= 0) {
        return renderAlert(
            <span>
                No data to show. There are no Assignment Plans with Climate surveys toggled on for{' '}
                <b>Time {currTimepoint}</b>.
            </span>
        );
    }

    if (!Object.values(climatePlanData).some(plan => plan.isSelected)) {
        return renderAlert(
            <span>
                No data to show. There are no Classes that have an Assignment Plan selected which contains Climate
                surveys in <b>Time {currTimepoint}</b>.
            </span>
        );
    }

    if (!Object.values(climatePlanData).some(plan => plan.hasOpened)) {
        const earliestOpens = Object.values(climatePlanData).reduce((earliest, curr) => {
            if (!curr.isSelected) {
                return earliest;
            }

            if (new Date(curr.opensDate) < new Date(earliest)) {
                earliest = curr.opensDate;
            }

            return earliest;
        }, '2099-01-01');
        return renderAlert(
            <span>
                No data to show. Data collection for <b>Time {currTimepoint}</b> will begin on <b>{earliestOpens}</b>{' '}
                for Students taking Climate surveys.
            </span>
        );
    }

    return (
        <div>
            {renderTimepointPills()}
            <ClimateReport currTimepoint={currTimepoint} />
        </div>
    );
}

export default ClimateTab;
